<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Trash, Copy, Pause, Play, X, Star } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableDate } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useTemplatesStore } from "@/stores/mailing/templates";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import type { Template } from "@/types";

  const templatesStore = useTemplatesStore();
  const { templates, templatesCount, currentPage } = storeToRefs(templatesStore);
  const { changePage, removeTemplate, duplicateTemplate, activateTemplate, deactivateTemplate, setAsBaseTemplate, unsetAsBaseTemplate } =
    templatesStore;
  const { isAdmin, userAccountIds } = storeToRefs(useCurrentUserStore());
  const { openRow } = useRowNavigation();

  const showAccountName = computed(() => isAdmin.value || (userAccountIds.value?.length ?? 0) > 1);

  const columns: DataTableColumn<Template>[] = [
    { key: "name", label: "Template" },
    { key: "status", label: "Status", width: 110 },
    { key: "date", label: "Date", width: 90 },
  ];

  const actionsFor = (template: Template) => [
    { isDelete: true, function: removeTemplate, msgConfirm: "this template", text: "delete", icon: Trash },
    { isDelete: false, function: duplicateTemplate, text: "duplicate", icon: Copy },
    { isDelete: false, function: deactivateTemplate, text: "deactivate", icon: Pause, condition: template.active },
    { isDelete: false, function: activateTemplate, text: "activate", icon: Play, condition: !template.active },
    { isDelete: false, function: unsetAsBaseTemplate, text: "unset as base", icon: X, condition: template.base },
    { isDelete: false, function: setAsBaseTemplate, text: "set as base", icon: Star, condition: !template.base },
  ];
</script>

<template>
  <IndexTable
    :items="templates"
    :columns="columns"
    :current-page="currentPage"
    :total="templatesCount"
    actions-class="w-[60px]"
    item-key="token"
    empty-message="No template yet."
    @row-click="(template, event) => openRow(`/mailing/templates/${template.token}/edit`, event)"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName
        :name="item.name"
        :subtitle="showAccountName ? item.account_name : null">
        <template #leading>
          <Tooltip
            v-if="item.base"
            content="Base template">
            <Star class="size-4 fill-status-warning text-status-warning" />
          </Tooltip>
        </template>
      </IndexTableName>
    </template>
    <template #cell-status="{ item }">
      <Badge
        :variant="item.active ? 'success' : 'neutral'"
        class="text-2xs font-semibold capitalize">
        {{ item.active ? "active" : "inactive" }}
      </Badge>
    </template>
    <template #cell-date="{ item }">
      <IndexTableDate :date="item.created_at" />
    </template>
    <template #actions="{ item }">
      <div
        class="flex justify-end"
        @click.stop>
        <ActionsDropdown
          :actions="actionsFor(item)"
          :token="item.token" />
      </div>
    </template>
  </IndexTable>
</template>
